/*============================================================================
  Ractive Blocks — clean-room custom elements for the ported NEFER/Shrine
  product-page blocks. The original behaviour lived in Shrine's remote,
  license-locked engine (js.shrinetheme.com) which Caldris removed; these are
  self-hosted re-implementations driven purely by the markup the blocks emit.
  No external requests, no tokens. © Ractiveone. Free to modify.

  Implemented:
    <content-tabs>   ← block product_tabs           (tab switching)
    <dynamic-dates>  ← block product_estimated-shipping (delivery date range)

  TODO (other ported blocks still emitting Shrine elements, currently unplaced):
    <clickable-discount>, <sticky-atc>, <secondary-variant-select>,
    <shrine-subscription-widget>, <splide-component>
==============================================================================*/
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     <content-tabs> — product_tabs block
     Markup contract:
       <content-tabs data-product-tabs="true">
         .tabs-header > button.tab-button.content-tab-button-js[data-index]
         .tabs-content > .content-tab.tab__content[data-index] (+ .content-tab--active)
     First button/panel carry the active class on render. Clicking a button
     activates the matching panel by data-index.
     -------------------------------------------------------------------- */
  if (!customElements.get('content-tabs')) {
    class RactiveContentTabs extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.buttons = Array.prototype.slice.call(
          this.querySelectorAll('.content-tab-button-js')
        );
        this.panels = Array.prototype.slice.call(
          this.querySelectorAll('.content-tab')
        );
        var self = this;
        this.buttons.forEach(function (btn) {
          btn.addEventListener('click', function () {
            self.activate(btn.getAttribute('data-index'));
          });
        });
      }
      activate(index) {
        this.buttons.forEach(function (b) {
          b.classList.toggle('active', b.getAttribute('data-index') === index);
        });
        this.panels.forEach(function (p) {
          p.classList.toggle(
            'content-tab--active',
            p.getAttribute('data-index') === index
          );
        });
      }
    }
    customElements.define('content-tabs', RactiveContentTabs);
  }

  /* ----------------------------------------------------------------------
     <dynamic-dates> — product_estimated-shipping block
     Computes an earliest/latest delivery date by adding min/max business
     days to today, skipping configured weekdays and specific MM-DD dates,
     then formats them with merchant-supplied day/month labels + format, and
     substitutes [start_date]/[end_date] into the message.
     -------------------------------------------------------------------- */
  if (!customElements.get('dynamic-dates')) {
    var DAY_KEYS = [
      'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
    ];

    function pad(n) { return n < 10 ? '0' + n : '' + n; }

    function ordinal(n) {
      var s = ['th', 'st', 'nd', 'rd'];
      var v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }

    class DynamicDates extends HTMLElement {
      connectedCallback() {
        if (this._done) return;
        this._done = true;
        try { this.render(); } catch (e) { /* fail soft: leave fallback text */ }
      }

      render() {
        var target = this.querySelector('[data-dynamic-date]');
        if (!target) return;

        var fmt = this.getAttribute('data-date-format') || 'day_mm_dd';
        var days = splitList(this.getAttribute('data-day-labels'));
        var months = splitList(this.getAttribute('data-month-labels'));
        var skipDays = splitList(this.getAttribute('data-days-to-skip')).map(function (d) {
          return d.toLowerCase();
        });
        var skipDates = splitList(this.getAttribute('data-dates-to-skip'));

        var min = parseInt(target.getAttribute('data-min-days'), 10);
        var max = parseInt(target.getAttribute('data-max-days'), 10);
        if (isNaN(min)) min = 0;
        if (isNaN(max)) max = min;
        var text = target.getAttribute('data-text') || '';

        function isSkipped(d) {
          if (skipDays.indexOf(DAY_KEYS[d.getDay()]) !== -1) return true;
          var md = pad(d.getMonth() + 1) + '-' + pad(d.getDate());
          return skipDates.indexOf(md) !== -1;
        }

        function addBusinessDays(n) {
          var d = new Date();
          d.setHours(12, 0, 0, 0); // avoid DST edge cases
          if (n <= 0) return d;
          var added = 0, guard = 0;
          while (added < n && guard < 3650) {
            d.setDate(d.getDate() + 1);
            guard++;
            if (isSkipped(d)) continue;
            added++;
          }
          return d;
        }

        function format(d) {
          var weekday = days[(d.getDay() + 6) % 7] || ''; // labels are Monday-first
          var month = months[d.getMonth()] || '';
          var day = d.getDate();
          switch (fmt) {
            case 'day_mm_dd': return weekday + ', ' + month + ' ' + ordinal(day);
            case 'day_dd_mm': return weekday + ', ' + day + '. ' + month;
            case 'mm_dd': return month + ' ' + ordinal(day);
            case 'dd_mm': return day + '. ' + month;
            case 'dd_mm_no_dot': return day + ' ' + month;
            case 'day_dd_mm_numeric':
              return weekday + ', ' + pad(day) + '. ' + pad(d.getMonth() + 1) + '.';
            case 'dd_mm_numeric':
              return pad(day) + '. ' + pad(d.getMonth() + 1) + '.';
            default: return weekday + ', ' + month + ' ' + ordinal(day);
          }
        }

        var start = format(addBusinessDays(min));
        var end = format(addBusinessDays(max));
        target.innerHTML = text
          .replace(/\[start_date\]/g, start)
          .replace(/\[end_date\]/g, end);
      }
    }

    function splitList(v) {
      if (!v) return [];
      return v.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }

    customElements.define('dynamic-dates', DynamicDates);
  }

  /* ----------------------------------------------------------------------
     <splide-component> — clean-room carousel
     Replaces Shrine's bundled Splide library (removed with the remote engine).
     Consumes the existing Splide DOM the blocks emit:
       <splide-component data-...>
         .splide > .splide__track > .splide__list > li.splide__slide
         .splide__dots-and-arrows > ul.splide__pagination + .splide__arrows
     Supports: type slide|loop|fade, responsive slides-per-view + gap,
     per-move, autoplay (+speed), arrows, dots, pointer drag, adaptive height
     (1-per-view), side-padding preview, and breakpoint "destroy" (static grid).
     Advanced Splide options not 1:1 reproduced are approximated and noted.
     -------------------------------------------------------------------- */
  if (!customElements.get('splide-component')) {
    var DESKTOP_MQ = '(min-width: 750px)';

    function intAttr(el, name, dflt) {
      var v = parseFloat(el.getAttribute(name));
      return isNaN(v) ? dflt : v;
    }
    function boolAttr(el, name) {
      var v = el.getAttribute(name);
      return v === 'true' || v === '' && el.hasAttribute(name);
    }

    class SplideComponent extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.root = this.querySelector('.splide');
        this.track = this.querySelector('.splide__track');
        this.list = this.querySelector('.splide__list');
        if (!this.root || !this.track || !this.list) return;
        this.slides = Array.prototype.slice.call(
          this.list.querySelectorAll(':scope > .splide__slide')
        );
        if (this.slides.length === 0) return;

        this.type = this.getAttribute('data-type') || 'slide';
        this.index = 0;
        this._mq = window.matchMedia(DESKTOP_MQ);
        this._onResize = this.layout.bind(this);
        this._onMq = this.layout.bind(this);

        this.buildControls();
        this.bindDrag();
        this.layout();

        window.addEventListener('resize', this._onResize);
        if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMq);
        else if (this._mq.addListener) this._mq.addListener(this._onMq);

        // pause autoplay on hover / when tab hidden
        this.addEventListener('mouseenter', this.stopAuto.bind(this));
        this.addEventListener('mouseleave', this.maybeAuto.bind(this));
      }

      disconnectedCallback() {
        this.stopAuto();
        window.removeEventListener('resize', this._onResize);
        if (this._mq && this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMq);
        else if (this._mq && this._mq.removeListener) this._mq.removeListener(this._onMq);
      }

      /* --- current responsive settings --- */
      cfg() {
        var d = this._mq.matches;
        return {
          desktop: d,
          perView: this.type === 'fade' ? 1 : Math.max(1, intAttr(this, d ? 'data-slides-desktop' : 'data-slides-mobile', 1)),
          perMove: Math.max(1, intAttr(this, d ? 'data-per-move-desktop' : 'data-per-move-mobile', 1)),
          gap: intAttr(this, d ? 'data-gap-desktop' : 'data-gap-mobile', 0),
          sidePad: intAttr(this, d ? 'data-side-padding-desktop' : 'data-side-padding-mobile', 0),
          adaptive: boolAttr(this, d ? 'data-desktop-adaptive-height' : 'data-mobile-adaptive-height'),
          destroy: boolAttr(this, d ? 'data-destroy-desktop' : 'data-destroy-mobile')
        };
      }

      buildControls() {
        var arrows = this.querySelector('.splide__arrows');
        var pagination = this.querySelector('.splide__pagination');
        if (arrows && !arrows.children.length) {
          arrows.innerHTML =
            '<button type="button" class="splide__arrow splide__arrow--prev" aria-label="Previous">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
            '<button type="button" class="splide__arrow splide__arrow--next" aria-label="Next">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
          var self = this;
          arrows.querySelector('.splide__arrow--prev').addEventListener('click', function () { self.move(-1); });
          arrows.querySelector('.splide__arrow--next').addEventListener('click', function () { self.move(1); });
        }
        this.arrowsEl = arrows;
        this.paginationEl = pagination;
        // color schemes
        var aColor = this.getAttribute('data-arrows-color');
        var dColor = this.getAttribute('data-dots-color');
        if (arrows && aColor) arrows.classList.add('color-' + aColor);
        if (pagination && dColor) pagination.classList.add('color-' + dColor);
      }

      renderDots(pages) {
        if (!this.paginationEl) return;
        if (this.paginationEl.children.length === pages) return;
        var self = this, html = '';
        for (var i = 0; i < pages; i++) {
          html += '<li><button type="button" class="splide__pagination__page" data-page="' + i + '" aria-label="Go to slide ' + (i + 1) + '"></button></li>';
        }
        this.paginationEl.innerHTML = html;
        Array.prototype.forEach.call(this.paginationEl.querySelectorAll('button'), function (btn) {
          btn.addEventListener('click', function () { self.goto(parseInt(btn.getAttribute('data-page'), 10) * self._perMove); });
        });
      }

      /* --- layout & positioning --- */
      layout() {
        var c = this.cfg();
        this._cfg = c;
        this._perMove = c.perMove;

        if (c.destroy) {
          this.root.classList.add('is-static');
          this.list.style.transform = '';
          this.slides.forEach(function (s) { s.style.width = ''; s.style.opacity = ''; });
          this.track.style.height = '';
          this.stopAuto();
          if (this.arrowsEl) this.arrowsEl.style.display = 'none';
          if (this.paginationEl) this.paginationEl.style.display = 'none';
          return;
        }
        this.root.classList.remove('is-static');
        if (this.arrowsEl) this.arrowsEl.style.display = '';
        if (this.paginationEl) this.paginationEl.style.display = '';

        this.track.style.paddingLeft = c.sidePad ? c.sidePad + 'px' : '';
        this.track.style.paddingRight = c.sidePad ? c.sidePad + 'px' : '';

        if (this.type === 'fade') {
          this.layoutFade();
        } else {
          this.layoutSlide(c);
        }

        var maxIndex = Math.max(0, this.slides.length - c.perView);
        if (this.index > maxIndex) this.index = maxIndex;
        var pages = Math.max(1, Math.ceil((this.slides.length - c.perView) / c.perMove) + 1);
        this.renderDots(pages);
        this.update();
        this.maybeAuto();
      }

      layoutSlide(c) {
        var inner = this.track.clientWidth - (c.sidePad * 2);
        var slideW = (inner - (c.perView - 1) * c.gap) / c.perView;
        this._step = slideW + c.gap;
        this.list.style.display = 'flex';
        this.list.style.columnGap = c.gap + 'px';
        var self = this;
        this.slides.forEach(function (s) {
          s.style.flex = '0 0 ' + slideW + 'px';
          s.style.width = slideW + 'px';
          s.style.opacity = '';
          s.style.position = '';
          self._tIndex = 0;
        });
      }

      layoutFade() {
        var self = this;
        var h = 0;
        this.list.style.display = 'block';
        this.list.style.position = 'relative';
        this.slides.forEach(function (s, i) {
          s.style.width = '100%';
          s.style.flex = '';
          s.style.position = i === 0 ? 'relative' : 'absolute';
          s.style.top = 0; s.style.left = 0;
          s.style.opacity = i === self.index ? '1' : '0';
          s.style.transition = 'opacity .4s ease';
          h = Math.max(h, s.offsetHeight);
        });
      }

      update() {
        var c = this._cfg;
        if (this.type === 'fade') {
          var self = this;
          this.slides.forEach(function (s, i) { s.style.opacity = i === self.index ? '1' : '0'; });
        } else {
          this.list.style.transition = 'transform .4s cubic-bezier(.16,1,.3,1)';
          this.list.style.transform = 'translateX(' + (-this.index * this._step) + 'px)';
          if (c.adaptive && c.perView === 1 && this.slides[this.index]) {
            this.track.style.height = this.slides[this.index].offsetHeight + 'px';
          }
        }
        // active dot
        if (this.paginationEl) {
          var active = Math.round(this.index / this._perMove);
          Array.prototype.forEach.call(this.paginationEl.querySelectorAll('button'), function (b, i) {
            b.classList.toggle('is-active', i === active);
            b.setAttribute('aria-current', i === active ? 'true' : 'false');
          });
        }
      }

      move(dir) {
        var c = this._cfg;
        var maxIndex = Math.max(0, this.slides.length - c.perView);
        var next = this.index + dir * this._perMove;
        if (this.type === 'loop') {
          if (next < 0) next = maxIndex;
          else if (next > maxIndex) next = 0;
        } else {
          next = Math.max(0, Math.min(maxIndex, next));
        }
        this.index = next;
        this.update();
      }

      goto(i) {
        var c = this._cfg;
        var maxIndex = Math.max(0, this.slides.length - c.perView);
        this.index = Math.max(0, Math.min(maxIndex, i));
        this.update();
      }

      /* --- autoplay --- */
      maybeAuto() {
        this.stopAuto();
        if (this._cfg && this._cfg.destroy) return;
        if (this.getAttribute('data-autoplay') !== 'true') return;
        if (this.slides.length <= (this._cfg ? this._cfg.perView : 1)) return;
        var speed = intAttr(this, 'data-autoplay-speed', 5) * 1000;
        var self = this;
        this._timer = setInterval(function () { self.move(1); }, Math.max(1500, speed));
      }
      stopAuto() { if (this._timer) { clearInterval(this._timer); this._timer = null; } }

      /* --- pointer drag --- */
      bindDrag() {
        var self = this, startX = 0, dragging = false, moved = 0;
        this.track.addEventListener('pointerdown', function (e) {
          if (self.type === 'fade' || (self._cfg && self._cfg.destroy)) return;
          dragging = true; startX = e.clientX; moved = 0;
          self.list.style.transition = 'none';
          self.stopAuto();
        });
        window.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          moved = e.clientX - startX;
          self.list.style.transform = 'translateX(' + (-self.index * self._step + moved) + 'px)';
        });
        window.addEventListener('pointerup', function () {
          if (!dragging) return;
          dragging = false;
          if (Math.abs(moved) > 40) self.move(moved < 0 ? 1 : -1);
          else self.update();
          self.maybeAuto();
        });
      }
    }
    customElements.define('splide-component', SplideComponent);
  }

  /* ----------------------------------------------------------------------
     <internal-video> — native (Shopify-hosted) video player
     Replaces Shrine's removed internal-video. Consumes the markup from
     snippets/video-player.liquid: a <video> with <source> (lazy via data-src
     when muted autoplay), a .internal-video__play overlay button, an optional
     .internal-video__timeline (progress via --completed) and an optional
     .internal-video__sound-btn (mute toggle via .internal-video--muted).
     Muted-autoplay plays in view / pauses out of view; data-action-on-inactive
     (pause|mute|none) controls behaviour when scrolled away.
     -------------------------------------------------------------------- */
  if (!customElements.get('internal-video')) {
    class InternalVideo extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.playBtn = this.querySelector('.internal-video__play');
        this.soundBtn = this.querySelector('.internal-video__sound-btn');
        this.timeline = this.querySelector('.internal-video__timeline');
        this.autoplay = this.getAttribute('data-autoplay') === 'true';
        this.inactive = this.getAttribute('data-action-on-inactive') || 'none';
        this._loaded = false;

        var self = this;
        if (this.playBtn) this.playBtn.addEventListener('click', function () { self.toggle(); });
        if (this.soundBtn) this.soundBtn.addEventListener('click', function (e) {
          e.stopPropagation(); self.toggleSound();
        });
        this.video.addEventListener('play', function () { self.classList.add('internal-video--playing'); });
        this.video.addEventListener('pause', function () { self.classList.remove('internal-video--playing'); });
        if (this.timeline) {
          this.video.addEventListener('timeupdate', function () {
            var d = self.video.duration;
            if (d) self.timeline.style.setProperty('--completed', (self.video.currentTime / d * 100) + '%');
          });
        }

        if (this.autoplay && 'IntersectionObserver' in window) {
          this._io = new IntersectionObserver(function (entries) {
            entries.forEach(function (en) {
              if (en.isIntersecting) {
                self.loadSources();
                self.video.play().catch(function () {});
              } else {
                if (self.inactive === 'mute') self.video.muted = true;
                else self.video.pause();
              }
            });
          }, { threshold: 0.25 });
          this._io.observe(this);
        }
      }

      disconnectedCallback() { if (this._io) this._io.disconnect(); }

      loadSources() {
        if (this._loaded) return;
        var changed = false;
        Array.prototype.forEach.call(this.video.querySelectorAll('source'), function (s) {
          if (s.dataset.src && !s.getAttribute('src')) { s.src = s.dataset.src; changed = true; }
        });
        if (changed) this.video.load();
        this._loaded = true;
      }

      toggle() {
        this.loadSources();
        if (this.video.paused) this.video.play().catch(function () {});
        else this.video.pause();
      }

      toggleSound() {
        this.video.muted = !this.video.muted;
        this.classList.toggle('internal-video--muted', this.video.muted);
      }
    }
    customElements.define('internal-video', InternalVideo);
  }

  /* ----------------------------------------------------------------------
     <countdown-timer> — cart-drawer evergreen MM:SS timer
     <countdown-timer data-duration="10"> (minutes). Persists per session so it
     doesn't reset on every drawer open.
     -------------------------------------------------------------------- */
  if (!customElements.get('countdown-timer')) {
    class CountdownTimer extends HTMLElement {
      connectedCallback() {
        if (this._t) return;
        var secs = parseFloat(this.getAttribute('data-duration')) || 600; // data-duration is in SECONDS
        var key = 'ractive-cart-timer';
        var end = parseInt(sessionStorage.getItem(key), 10);
        var now = Date.now();
        // reset if missing, expired, or stale (stored end implies more time than the configured duration)
        if (!end || end < now || (end - now) > secs * 1000 + 2000) {
          end = now + secs * 1000;
          try { sessionStorage.setItem(key, end); } catch (e) {}
        }
        var self = this;
        function tick() {
          var rem = Math.max(0, end - Date.now());
          var m = Math.floor(rem / 60000), s = Math.floor((rem % 60000) / 1000);
          self.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
          if (rem <= 0 && self._t) { clearInterval(self._t); self._t = null; }
        }
        tick();
        this._t = setInterval(tick, 1000);
      }
      disconnectedCallback() { if (this._t) { clearInterval(this._t); this._t = null; } }
    }
    customElements.define('countdown-timer', CountdownTimer);
  }

  /* ----------------------------------------------------------------------
     <tnc-chekcbox> — terms & conditions checkbox that can gate checkout
     -------------------------------------------------------------------- */
  if (!customElements.get('tnc-chekcbox')) {
    function checkoutButtons() {
      return Array.prototype.slice.call(
        document.querySelectorAll('#CartDrawer-Checkout, [name="checkout"], .cart__checkout-button')
      );
    }
    class TncCheckbox extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.section = this.getAttribute('data-section');
        this.gate = this.getAttribute('data-disable-button') === 'true';
        this.setAttribute('role', 'checkbox');
        this.setAttribute('tabindex', '0');
        this.setAttribute('aria-checked', this.getAttribute('data-checked') === 'true');
        var self = this;
        this.addEventListener('click', function () { self.toggle(); });
        this.addEventListener('keydown', function (e) {
          if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); self.toggle(); }
        });
        if (this.gate) {
          // intercept checkout while unchecked, show the warning instead
          this._onCheckout = function (e) {
            if (self.getAttribute('data-checked') !== 'true') {
              e.preventDefault();
              e.stopImmediatePropagation();
              self.showWarning();
              self.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          };
          checkoutButtons().forEach(function (b) { b.addEventListener('click', self._onCheckout, true); });
        }
      }
      toggle() {
        var on = this.getAttribute('data-checked') === 'true';
        this.setAttribute('data-checked', on ? 'false' : 'true');
        this.setAttribute('aria-checked', !on);
        if (!on) this.hideWarning();
      }
      warningEl() { return document.querySelector('.tnc-checkbox-warning-' + this.section); }
      showWarning() { var w = this.warningEl(); if (w) { w.textContent = this.getAttribute('data-warning-text') || ''; w.classList.remove('hidden'); } }
      hideWarning() { var w = this.warningEl(); if (w) w.classList.add('hidden'); }
    }
    customElements.define('tnc-chekcbox', TncCheckbox);
  }

  /* ----------------------------------------------------------------------
     <cart-note> — saves the cart note (debounced) via cart/update.js
     -------------------------------------------------------------------- */
  if (!customElements.get('cart-note')) {
    class CartNote extends HTMLElement {
      connectedCallback() {
        var ta = this.querySelector('textarea');
        if (!ta) return;
        var t;
        ta.addEventListener('input', function () {
          clearTimeout(t);
          t = setTimeout(function () {
            fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: ta.value })
            }).catch(function () {});
          }, 500);
        });
      }
    }
    customElements.define('cart-note', CartNote);
  }

  /* ----------------------------------------------------------------------
     <date-countdown-timer> — countdown to a fixed end time, or an evergreen
     repeat (data-repeat-hours). Updates .countdown-timer__number elements in
     d/h/m/s order (text "00" or two .countdown-timer__digit spans).
     -------------------------------------------------------------------- */
  if (!customElements.get('date-countdown-timer')) {
    function pad2(n) { n = Math.max(0, n); return n < 10 ? '0' + n : '' + n; }
    class DateCountdownTimer extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.nums = Array.prototype.slice.call(this.querySelectorAll('.countdown-timer__number'));
        if (!this.nums.length) return;
        this.show = {
          d: this.getAttribute('data-show-days') === 'true',
          h: this.getAttribute('data-show-hours') === 'true',
          m: this.getAttribute('data-show-minutes') === 'true',
          s: this.getAttribute('data-show-seconds') === 'true'
        };
        this.units = [];
        if (this.show.d) this.units.push('d');
        if (this.show.h) this.units.push('h');
        if (this.show.m) this.units.push('m');
        if (this.show.s) this.units.push('s');
        this.behavior = this.getAttribute('data-end-behavior') || 'hide';
        this.repeatHrs = parseFloat(this.getAttribute('data-repeat-hours')) || 0;
        this.endMs = this.computeEnd();
        if (!this.endMs) return;
        var self = this;
        this.update();
        this._t = setInterval(function () { self.update(); }, 1000);
      }
      disconnectedCallback() { if (this._t) clearInterval(this._t); }

      computeEnd() {
        if (this.repeatHrs > 0) {
          // evergreen: persist a start per timer and repeat every N hours
          var key = 'ractive-cd-' + (this.id || 'x');
          var start = parseInt(sessionStorage.getItem(key), 10);
          var now = Date.now();
          var span = this.repeatHrs * 3600000;
          if (!start || now - start >= span) { start = now; try { sessionStorage.setItem(key, start); } catch (e) {} }
          return start + span;
        }
        var raw = (this.getAttribute('data-end-time') || '').trim();
        if (!raw) return 0;
        var t = Date.parse(raw.replace(' ', 'T'));
        if (isNaN(t)) t = Date.parse(raw);
        return isNaN(t) ? 0 : t;
      }

      ended() {
        clearInterval(this._t); this._t = null;
        if (this.behavior === 'hide') {
          var sec = this.closest('.shopify-section') || this;
          this.style.display = 'none';
        }
        // 'zeros' / default: leave at 00; merchant may also show a message via CSS
      }

      update() {
        var rem = Math.floor((this.endMs - Date.now()) / 1000);
        if (rem <= 0) { this.setAll(0, 0, 0, 0); this.ended(); return; }
        var t = rem, vals = {};
        if (this.show.d) { vals.d = Math.floor(t / 86400); t -= vals.d * 86400; }
        if (this.show.h) { vals.h = Math.floor(t / 3600); t -= vals.h * 3600; }
        if (this.show.m) { vals.m = Math.floor(t / 60); t -= vals.m * 60; }
        if (this.show.s) { vals.s = t; }
        var self = this;
        this.units.forEach(function (u, i) { self.setNum(self.nums[i], vals[u]); });
      }
      setAll() { var self = this; this.units.forEach(function (u, i) { self.setNum(self.nums[i], 0); }); }
      setNum(el, val) {
        if (!el) return;
        var str = pad2(val);
        var digits = el.querySelectorAll('.countdown-timer__digit');
        if (digits.length >= 2) {
          digits[0].textContent = str.charAt(str.length - 2) || '0';
          digits[1].textContent = str.charAt(str.length - 1);
        } else {
          el.textContent = str;
        }
      }
    }
    customElements.define('date-countdown-timer', DateCountdownTimer);
  }

  /* ----------------------------------------------------------------------
     <quantity-breaks> — recompute each tier's price/compare/savings when the
     selected variant changes (Dawn publishes PUB_SUB_EVENTS.variantChange). The
     server renders the initial values; this keeps them correct across variants.
     Tier math: unit = round(variant.price * percentage_left) - fixed_discount;
     line = unit * qty; compare = compare_unit * qty (per data-compare-mode).
     Substitutes into each .variant-price-update element's data-text template
     ([quantity], [price], [compare_price], [amount_saved], [price_each], ...).
     -------------------------------------------------------------------- */
  if (!customElements.get('quantity-breaks')) {
    function fmtAmount(cents, decimals, thousands, decimalSep) {
      var neg = cents < 0; cents = Math.abs(cents);
      var v = (cents / 100).toFixed(decimals).split('.');
      v[0] = v[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (neg ? '-' : '') + v[0] + (decimals ? decimalSep + v[1] : '');
    }
    function money(cents, fmt) {
      fmt = fmt || '${{amount}}';
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, n) {
        switch (n) {
          case 'amount': return fmtAmount(cents, 2, ',', '.');
          case 'amount_no_decimals': return fmtAmount(cents, 0, ',', '.');
          case 'amount_with_comma_separator': return fmtAmount(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fmtAmount(cents, 0, '.', ',');
          case 'amount_with_space_separator': return fmtAmount(cents, 2, ' ', ',');
          case 'amount_no_decimals_with_space_separator': return fmtAmount(cents, 0, ' ', ',');
          case 'amount_with_apostrophe_separator': return fmtAmount(cents, 2, "'", '.');
          default: return fmtAmount(cents, 2, ',', '.');
        }
      });
    }
    class QuantityBreaks extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.section = this.getAttribute('data-section');
        this.fmt = this.getAttribute('data-money-format') || '${{amount}}';
        var self = this;
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, function (e) {
            var d = e && e.data ? e.data : {};
            if (d.sectionId && self.section && d.sectionId !== self.section) return;
            if (d.variant) self.render(d.variant);
          });
        }
      }
      disconnectedCallback() { if (this._unsub) this._unsub(); }
      render(variant) {
        if (!variant || typeof variant.price !== 'number') return;
        var self = this;
        Array.prototype.forEach.call(this.querySelectorAll('.quantity-break'), function (label) {
          var qty = parseInt(label.getAttribute('data-quantity'), 10) || 1;
          var pl = parseFloat(label.getAttribute('data-percentage-left'));
          if (isNaN(pl)) pl = 1;
          var fd = parseInt(label.getAttribute('data-fixed-discount'), 10) || 0;
          var mode = label.getAttribute('data-compare-mode') || 'compare_price';
          var unit = Math.round(variant.price * pl) - fd;
          if (unit < 0) unit = 0;
          var compUnit;
          if (mode === 'compare_price') {
            compUnit = (variant.compare_at_price && variant.compare_at_price > unit) ? variant.compare_at_price : 0;
          } else { // main_price: compare against the variant's regular price
            compUnit = variant.price > unit ? variant.price : 0;
          }
          var linePrice = unit * qty;
          var lineCompare = compUnit ? compUnit * qty : 0;
          var saved = lineCompare > linePrice ? lineCompare - linePrice : 0;
          var repl = {
            quantity: qty,
            price: money(linePrice, self.fmt),
            price_each: money(unit, self.fmt),
            compare_price: money(lineCompare, self.fmt),
            compare_price_each: money(compUnit, self.fmt),
            amount_saved: money(saved, self.fmt)
          };
          Array.prototype.forEach.call(label.querySelectorAll('.variant-price-update'), function (el) {
            var tpl = el.getAttribute('data-text');
            if (tpl == null) return;
            var html = tpl;
            for (var k in repl) html = html.split('[' + k + ']').join(repl[k]);
            el.innerHTML = html;
          });
          // hide the compare price + savings caption when there is no saving
          var hide = saved <= 0;
          var cmp = label.querySelector('.quantity-break__compare-price');
          var cap = label.querySelector('.quantity-break__caption');
          if (cmp) cmp.style.display = hide ? 'none' : '';
          if (cap) cap.style.display = hide ? 'none' : '';
        });
      }
    }
    customElements.define('quantity-breaks', QuantityBreaks);
  }

  /* <quantity-break-upsells> — defined so it's a known element; per-tier upsell
     add-to-cart is configured by the merchant and handled inline when present. */
  if (!customElements.get('quantity-break-upsells')) {
    customElements.define('quantity-break-upsells', class extends HTMLElement {});
  }

  /* ======================================================================
     Shared cart helpers for the gift widgets. Mirror Dawn's section-render
     flow (POST with `sections`, swap innerHTML) WITHOUT force-opening the
     drawer, so background gift sync is silent. © Ractiveone
     ====================================================================== */
  var RB_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  function rbRoute(name, fallback) {
    return (window.routes && window.routes[name]) ? window.routes[name] : fallback;
  }
  function rbGetCart() {
    return fetch(rbRoute('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function rbCartAdd(items) {
    return fetch(rbRoute('cart_add_url', '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: RB_SECTIONS })
    }).then(function (r) { return r.json(); });
  }
  function rbCartChange(id, quantity) {
    return fetch(rbRoute('cart_change_url', '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: id, quantity: quantity, sections: RB_SECTIONS })
    }).then(function (r) { return r.json(); });
  }
  function rbSectionInner(html, selector) {
    if (!html) return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector) || doc.querySelector('.shopify-section');
    return el ? el.innerHTML : null;
  }
  function rbRenderCart(json) {
    if (json && json.sections) {
      [{ id: 'cart-drawer', sel: '#CartDrawer' }, { id: 'cart-icon-bubble', sel: null }].forEach(function (s) {
        var node = s.sel ? document.querySelector(s.sel) : document.getElementById(s.id);
        if (!node) return;
        var inner = rbSectionInner(json.sections[s.id], s.sel || '.shopify-section');
        if (inner != null) node.innerHTML = inner;
      });
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ractive-gifts', cartData: json });
    }
  }
  function rbIsGiftLine(li) {
    return li && li.properties && (li.properties._gift === 'true' || li.properties._gift === true);
  }

  /* ----------------------------------------------------------------------
     <cart-discount> — applies a discount code in the cart drawer.
     Shopify has no AJAX "apply discount" endpoint, so we GET /discount/{code}
     (which stores the code on the cart session) then re-render the cart
     sections. On an invalid code we show the inline error WITHOUT re-rendering
     (a re-render would replace this element and wipe the message). © Ractiveone
     -------------------------------------------------------------------- */
  function rbLocaleRoot() {
    var r = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return r.replace(/\/$/, ''); // '' or '/de'
  }
  function rbApplyDiscount(code) {
    return fetch(rbLocaleRoot() + '/discount/' + encodeURIComponent(code), {
      method: 'GET', headers: { Accept: 'text/html' }
    });
  }
  function rbGetCartSections() {
    return fetch(rbRoute('cart_url', '/cart') + '?sections=' + RB_SECTIONS.join(','), {
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); });
  }
  function rbDiscountApplied(cart, code) {
    var apps = [].concat(cart.cart_level_discount_applications || [], cart.discount_applications || []);
    var lc = code.toLowerCase();
    return apps.some(function (d) {
      return ((d.title || d.code || '') + '').toLowerCase() === lc;
    });
  }

  if (!customElements.get('cart-discount')) {
    customElements.define('cart-discount', class CartDiscount extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="cart-discount-field"]');
        this.errorEl = this.querySelector('.cart-discount-form__error');
        this.button = this.querySelector('button[type="submit"]');
        if (!this.form) return;
        this._onSubmit = this.onSubmit.bind(this);
        this._onInput = this.hideError.bind(this);
        this.form.addEventListener('submit', this._onSubmit);
        if (this.input) this.input.addEventListener('input', this._onInput);
      }
      disconnectedCallback() {
        if (this.form && this._onSubmit) this.form.removeEventListener('submit', this._onSubmit);
        if (this.input && this._onInput) this.input.removeEventListener('input', this._onInput);
      }
      onSubmit(e) {
        e.preventDefault();
        var self = this;
        var code = ((this.input && this.input.value) || '').trim();
        if (!code || this._busy) return;
        this._busy = true;
        this.setLoading(true);
        this.hideError();
        rbApplyDiscount(code)
          .then(function () { return rbGetCart(); })
          .then(function (cart) {
            if (rbDiscountApplied(cart, code)) {
              return rbGetCartSections().then(function (sections) {
                if (self.input) self.input.value = '';
                rbRenderCart({ sections: sections }); // replaces this element (drawer re-render)
              });
            }
            self.showError(); // keep current element so the message stays visible
          })
          .catch(function () { self.showError(); })
          .then(function () { self._busy = false; self.setLoading(false); });
      }
      setLoading(on) {
        if (!this.button) return;
        this.button.classList.toggle('loading', !!on);
        this.button.setAttribute('aria-busy', on ? 'true' : 'false');
        this.button.disabled = !!on;
      }
      showError() { if (this.errorEl) this.errorEl.hidden = false; }
      hideError() { if (this.errorEl) this.errorEl.hidden = true; }
    });
  }

  /* ----------------------------------------------------------------------
     <quantity-gifts> — "buy N items, get free gift(s)". Unlocks each gift
     tier when the non-gift cart quantity meets its threshold, and auto
     adds/removes the gift variant. Make the gift actually free with a
     Shopify automatic discount (Buy X Get Y) on those products.
     -------------------------------------------------------------------- */
  if (!customElements.get('quantity-gifts')) {
    class QuantityGifts extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.busy = false;
        this.gifts = Array.prototype.map.call(this.querySelectorAll('.quantity-gift'), function (g) {
          return {
            node: g,
            threshold: parseInt(g.getAttribute('data-quantity'), 10) || 0,
            variantId: parseInt(g.getAttribute('data-product'), 10) || 0
          };
        });
        var self = this;
        this._unsub = (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined')
          ? subscribe(PUB_SUB_EVENTS.cartUpdate, function (e) { if (e && e.source === 'ractive-gifts') return; self.evaluate(); })
          : null;
        this.evaluate();
      }
      disconnectedCallback() { if (this._unsub) this._unsub(); }
      evaluate() {
        if (this.busy || !this.gifts.length) return;
        var self = this;
        rbGetCart().then(function (cart) {
          var items = cart.items || [];
          var giftIds = self.gifts.map(function (g) { return g.variantId; });
          var qty = 0;
          items.forEach(function (li) {
            if (rbIsGiftLine(li) || giftIds.indexOf(li.variant_id) > -1) return;
            qty += li.quantity;
          });
          var addItems = [], removeIds = [];
          self.gifts.forEach(function (g) {
            if (!g.variantId) return;
            var inCart = items.some(function (li) { return li.variant_id === g.variantId; });
            var unlocked = g.threshold > 0 && qty >= g.threshold;
            g.node.setAttribute('data-unlocked', unlocked ? 'true' : 'false');
            g.node.classList.toggle('quantity-gift--unlocked', unlocked);
            if (unlocked && !inCart && !g.failed) addItems.push({ id: g.variantId, quantity: 1, properties: { _gift: 'true' } });
            if (!unlocked && inCart) removeIds.push(g.variantId);
          });
          if (!addItems.length && !removeIds.length) return;
          self.busy = true;
          var chain = Promise.resolve();
          removeIds.forEach(function (id) { chain = chain.then(function () { return rbCartChange(id, 0); }); });
          if (addItems.length) chain = chain.then(function () { return rbCartAdd(addItems); });
          chain.then(function (json) { rbRenderCart(json); })
            .catch(function () { /* transient failure: retry on the next cartUpdate instead of disabling permanently (CF-3b) */ })
            .then(function () { self.busy = false; });
        }).catch(function () {});
      }
    }
    customElements.define('quantity-gifts', QuantityGifts);
  }

  /* ----------------------------------------------------------------------
     <cart-drawer-gift> — single cart upsell gift unlocked by quantity or
     subtotal threshold (data-type, data-requirement). Auto adds/removes.
     -------------------------------------------------------------------- */
  if (!customElements.get('cart-drawer-gift')) {
    class CartDrawerGift extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.busy = false;
        this.variantId = parseInt(this.getAttribute('data-id'), 10) || 0;
        this.type = this.getAttribute('data-type') || 'product_quantity';
        this.requirement = parseFloat(this.getAttribute('data-requirement')) || 0;
        var self = this;
        this._unsub = (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined')
          ? subscribe(PUB_SUB_EVENTS.cartUpdate, function (e) { if (e && e.source === 'ractive-gifts') return; self.evaluate(); })
          : null;
        this.evaluate();
      }
      disconnectedCallback() { if (this._unsub) this._unsub(); }
      evaluate() {
        if (this.busy || !this.variantId) return;
        var self = this;
        rbGetCart().then(function (cart) {
          var items = cart.items || [];
          var inCart = items.some(function (li) { return li.variant_id === self.variantId; });
          var metric = 0;
          items.forEach(function (li) {
            if (rbIsGiftLine(li) || li.variant_id === self.variantId) return;
            metric += (self.type === 'cart_subtotal') ? li.final_line_price : li.quantity;
          });
          var unlocked = self.requirement > 0 && metric >= self.requirement;
          self.setAttribute('data-selected', unlocked ? 'true' : 'false');
          if (unlocked && !inCart && !self.failed) {
            self.busy = true;
            rbCartAdd([{ id: self.variantId, quantity: 1, properties: { _gift: 'true' } }])
              .then(function (json) { rbRenderCart(json); })
              .catch(function () { /* transient failure: retry on the next cartUpdate (CF-3b) */ })
              .then(function () { self.busy = false; });
          } else if (!unlocked && inCart) {
            self.busy = true;
            rbCartChange(self.variantId, 0)
              .then(function (json) { rbRenderCart(json); })
              .catch(function () {})
              .then(function () { self.busy = false; });
          }
        }).catch(function () {});
      }
    }
    customElements.define('cart-drawer-gift', CartDrawerGift);
  }

  /* ----------------------------------------------------------------------
     <card-variant-selects> — on-card variant switching. The option radios
     post option names, not a variant id, so without this the card's ATC
     would add the default variant. We resolve the selected variant from the
     card's variants JSON and write it into the form's hidden id input.
     -------------------------------------------------------------------- */
  if (!customElements.get('card-variant-selects')) {
    function rbFindVariants(start) {
      var n = start;
      while (n) {
        if (n.querySelector) {
          var s = n.querySelector('script[type="application/json"]');
          if (s) { try { var d = JSON.parse(s.textContent); if (Array.isArray(d)) return d; } catch (e) {} }
        }
        n = n.parentElement;
      }
      return null;
    }
    class CardVariantSelects extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.variants = rbFindVariants(this);
        this.formId = this.getAttribute('data-section');
        var self = this;
        this._onChange = function () { self.onChange(); };
        this.addEventListener('change', this._onChange);
      }
      disconnectedCallback() { this.removeEventListener('change', this._onChange); }
      onChange() {
        if (!this.variants) return;
        var values = [];
        this.querySelectorAll('.product-form__input').forEach(function (blk) {
          var c = blk.querySelector('input[type="radio"]:checked');
          if (c) values.push(c.value);
        });
        if (!values.length) return;
        var key = values.join('~~~');
        var variant = this.variants.find(function (v) { return (v.options || []).join('~~~') === key; });
        if (!variant) return;
        var form = this.formId ? document.getElementById(this.formId) : null;
        var idInput = form ? form.querySelector('input[name="id"]') : null;
        if (idInput) idInput.value = variant.id;
        if (form) {
          var submit = form.querySelector('[name="add"]');
          if (submit) submit.toggleAttribute('disabled', variant.available === false);
        }
        var media = variant.featured_media || (variant.featured_image ? { preview_image: variant.featured_image } : null);
        var src = media && media.preview_image ? media.preview_image.src : null;
        if (src) {
          var card = this.closest('.card-wrapper, .card, li.grid__item') || this.parentElement;
          var img = card ? card.querySelector('.card__media img, .media img') : null;
          if (img) { img.src = src.indexOf('width=') > -1 ? src : (src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=533'); img.removeAttribute('srcset'); }
        }
      }
    }
    customElements.define('card-variant-selects', CardVariantSelects);
  }

  /* ----------------------------------------------------------------------
     <custom-product-field> — write the field value into the product form as
     a line-item property (properties[name]) and enforce required fields on
     submit. Native inputs already submit; this adds the property + validation.
     -------------------------------------------------------------------- */
  if (!customElements.get('custom-product-field')) {
    class CustomProductField extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.fieldName = this.getAttribute('data-name');
        this.required = this.getAttribute('data-required') === 'true';
        this.section = this.getAttribute('data-section');
        this.form = this.closest('form[action*="/cart/add"]');
        if (!this.form && this.section) {
          this.form = document.querySelector('#product-form-' + this.section + ', form#product-form-' + this.section);
        }
        if (!this.form) { var pf = document.querySelector('product-form form'); this.form = pf || null; }
        if (this.fieldName && this.form) {
          this.hidden = document.createElement('input');
          this.hidden.type = 'hidden';
          this.hidden.name = 'properties[' + this.fieldName + ']';
          this.form.appendChild(this.hidden);
        }
        var self = this;
        this._onInput = function () { self.sync(); };
        this.addEventListener('input', this._onInput);
        this.addEventListener('change', this._onInput);
        this.sync();
        if (this.form) {
          this._onSubmit = function (e) {
            if (!self.validate()) { e.preventDefault(); e.stopImmediatePropagation(); self.showError(); }
          };
          this.form.addEventListener('submit', this._onSubmit, true);
        }
      }
      disconnectedCallback() {
        if (this.form && this._onSubmit) this.form.removeEventListener('submit', this._onSubmit, true);
        if (this.hidden && this.hidden.parentNode) this.hidden.parentNode.removeChild(this.hidden);
      }
      value() {
        var el = this.querySelector('input[type="radio"]:checked');
        if (el) return el.value;
        el = this.querySelector('select');
        if (el) return el.value;
        el = this.querySelector('input[type="text"], input[type="number"], textarea');
        return el ? el.value : '';
      }
      sync() { if (this.hidden) this.hidden.value = this.value(); this.clearError(); }
      validate() { var v = this.value(); return !this.required || (v != null && String(v).trim() !== ''); }
      showError() {
        this.classList.add('custom-product-field--error');
        var m = this.querySelector('.input-error-msg');
        if (m) m.style.display = 'block';
      }
      clearError() {
        this.classList.remove('custom-product-field--error');
        var m = this.querySelector('.input-error-msg');
        if (m) m.style.display = '';
      }
    }
    customElements.define('custom-product-field', CustomProductField);
  }

  /* <copy-button> — copy a target's id/text (or literal) to the clipboard. */
  if (!customElements.get('copy-button')) {
    class CopyButton extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        var self = this;
        this._onClick = function () {
          var sel = self.getAttribute('data-content');
          var text = sel || '';
          if (sel && sel.charAt(0) === '#') {
            var t = document.querySelector(sel);
            if (t) text = t.id || (t.innerText || '').trim();
          }
          var copied = false;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text); copied = true;
          } else {
            // fallback for non-secure (http) contexts where navigator.clipboard is undefined
            try {
              var ta = document.createElement('textarea');
              ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'absolute'; ta.style.left = '-9999px';
              document.body.appendChild(ta); ta.select();
              copied = document.execCommand('copy');
              document.body.removeChild(ta);
            } catch (e) { copied = false; }
          }
          if (!copied) return; // don't show success if nothing was copied (CF-4c)
          self.setAttribute('data-success', 'true');
          self.classList.add('copy-button--success');
          clearTimeout(self._t);
          self._t = setTimeout(function () {
            self.setAttribute('data-success', 'false');
            self.classList.remove('copy-button--success');
          }, 1800);
        };
        this.addEventListener('click', this._onClick);
      }
      disconnectedCallback() { this.removeEventListener('click', this._onClick); }
    }
    customElements.define('copy-button', CopyButton);
  }

  /* NOTE: do NOT define <product-component> (or <collection-component>). Shopify
     registers these at runtime via its StandardEvents view-tracking script
     (customElements.define('product-component', StandardEvents.createViewEventElement(...))).
     Defining them here raced ahead of Shopify's UNGUARDED define and threw
     "name has already been used", aborting StandardEvents init. Leave them to Shopify. */

  /* ----------------------------------------------------------------------
     <ractive-variant-boxes> — box-style variant selector (ported from the
     Sense "custom variant picker"). One box per first-option value, with an
     expandable sub-option button row; drives Dawn's native <variant-selects>
     by setting its inputs checked + dispatching a bubbling `change` from the
     input (verified to trigger product-info's price/media re-render). The
     native picker is hidden via the block CSS. © Ractiveone
     -------------------------------------------------------------------- */
  if (!customElements.get('ractive-variant-boxes')) {
    function rvbEsc(v) { return (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/"/g, '\\"'); }
    class RactiveVariantBoxes extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.section = this.getAttribute('data-section');
        var self = this;
        this.querySelectorAll('.custom-variant-picker-box-splitter').forEach(function (splitter) {
          var box = splitter.parentElement; // .custom-variant-picker-box
          box.addEventListener('click', function () {
            var variants = box.parentElement.querySelector('.custom-variant-picker-box-variants');
            if (!variants) { self.select(splitter, true); return; }
            self.select(variants.querySelector('button.active') || variants.querySelector('button'), false);
          });
        });
        this.querySelectorAll('.custom-variant-picker-box-variants > button').forEach(function (btn) {
          btn.addEventListener('click', function (e) { e.stopPropagation(); self.select(btn, false); });
        });
        this.syncDefault();
      }
      // re-query each time: Dawn replaces <variant-selects> on variant change
      vs() {
        return (this.section && document.querySelector('variant-selects[data-section="' + this.section + '"]')) ||
          document.querySelector('variant-selects');
      }
      setInputs(options) {
        var vs = this.vs();
        if (!vs) return null;
        // Scope each option value to its OWN option fieldset (by position) so two
        // options sharing a value string can't cross-match the wrong input. Falls
        // back to a global match if the picker isn't fieldset-structured. (PV-3)
        var fieldsets = vs.querySelectorAll('fieldset');
        var last = null;
        options.forEach(function (v, i) {
          var scope = fieldsets[i] || vs;
          var inp = scope.querySelector('input[value="' + rvbEsc(v) + '"]');
          if (inp) { inp.checked = true; last = inp; return; }
          var opt = scope.querySelector('option[value="' + rvbEsc(v) + '"]'); // dropdown picker
          if (opt) {
            var sel = opt.closest('select');
            if (sel) { sel.value = opt.value; last = sel; }
          }
        });
        return { vs: vs, last: last };
      }
      select(el, isRoot) {
        if (!el) return;
        var splitter = isRoot ? el : el.closest('.custom-variant-picker-box-wrapper').querySelector('.custom-variant-picker-box-splitter');
        var json = el.getAttribute('data-options');
        if (!json) return;
        var options;
        try { options = JSON.parse(json); } catch (e) { return; }
        var prev = this.querySelector('.custom-variant-picker-box-splitter.active');
        if (prev && prev !== splitter) prev.classList.remove('active');
        splitter.classList.add('active');
        if (!isRoot) {
          var grp = el.parentElement;
          grp.querySelectorAll('.active').forEach(function (s) { s.classList.remove('active'); });
          el.classList.add('active');
          [['[data-comparison]', '.custom-variant-picker-box-compare'],
           ['[data-savings]', '.custom-variant-picker-box-saved'],
           ['[data-price]', '.custom-variant-picker-box-price']].forEach(function (p) {
            var src = el.querySelector(p[0]), dst = splitter.querySelector(p[1]);
            if (src && dst) dst.innerHTML = src.innerHTML;
          });
        }
        var r = this.setInputs(options);
        if (r) (r.last || r.vs).dispatchEvent(new Event('change', { bubbles: true }));
      }
      syncDefault() {
        if (!this.vs()) {
          // The box picker drives the native <variant-selects>; without it, ATC
          // would silently fall back to the first variant. Warn so it's caught. (CF-2)
          if (window.console) console.warn('[ractive-variant-boxes] no <variant-selects> on the page — add the default Variant picker block so the box picker can drive variant selection.');
        }
        var active = this.querySelector('.custom-variant-picker-box-splitter.active');
        if (!active) {
          // default_selected was 0 / out-of-range -> nothing was marked active.
          // Fall back to the first box so a selection is always shown + applied. (CF-2)
          active = this.querySelector('.custom-variant-picker-box-splitter');
          if (active) active.classList.add('active');
        }
        if (!active) return;
        var wrap = active.closest('.custom-variant-picker-box-wrapper');
        var variants = wrap ? wrap.querySelector('.custom-variant-picker-box-variants') : null;
        var el = variants ? (variants.querySelector('button.active') || variants.querySelector('button')) : active;
        var json = el && el.getAttribute('data-options');
        if (!json) return;
        var options;
        try { options = JSON.parse(json); } catch (e) { return; }
        var r = this.setInputs(options);
        // only force a re-render when the default box isn't the first (server) variant
        var def = this.getAttribute('data-default');
        if (r && def && def !== '1') (r.last || r.vs).dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    customElements.define('ractive-variant-boxes', RactiveVariantBoxes);
  }
})();
